import React, {
  forwardRef,
  useState,
} from "react";

interface FormInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "value" | "onChange"
  > {
  label: string;

  value: string | number | "";

  onValueChange: (
    value: string
  ) => void;

  fieldRefs?: React.MutableRefObject<
    Record<
      string,
      HTMLElement | null
    >
  >;

  fieldKey?: string;

  onComplete?: () => void;

  readOnly?: boolean;

  tooltip?: string;

  placeholder?: string;

  required?: boolean;

  validation?:
  | "any"
  | "text"
  | "numeric"
  | "email";

  helperText?: string;

  error?: string;
  inputPrefix?: string;
  containerClassName?: string;
}

const FormInput = forwardRef<
  HTMLInputElement,
  FormInputProps
>(
  (
    {
      label,

      value,

      onValueChange,

      fieldRefs,

      fieldKey,

      onComplete,

      readOnly = false,

      tooltip,

      placeholder,

      required = false,

      validation = "any",

      helperText,

      error,

      inputPrefix: prefix,

      containerClassName = "",

      className = "",

      id,

      ...props
    },
    ref
  ) => {
    //----------------------------------------------------

    const [localError, setLocalError] =
      useState("");


    function validate(
      val: string
    ): string {

      const trimmed =
        val.trim();

      //------------------------------------------------
      // Required
      //------------------------------------------------

      if (
        required &&
        trimmed === ""
      ) {
        return `${label} is required.`;
      }

      //------------------------------------------------
      // Empty optional field
      //------------------------------------------------

      if (trimmed === "") {
        return "";
      }

      //------------------------------------------------
      // Validation rules
      //------------------------------------------------

      switch (validation) {

        case "numeric":

          if (
            !/^\d*\.?\d*$/.test(
              trimmed
            )
          ) {
            return "Only numbers are allowed.";
          }

          break;

        case "text":

        // Must contain at least one alphabet

        case "text":

          // Must contain at least one alphabet
          if (
            !/[A-Za-z]/.test(trimmed)
          ) {
            return `${label} must contain at least one alphabet.`;
          }

          // Allow letters, numbers, spaces and common punctuation
          // Useful for names, addresses, cities, descriptions etc.
          if (
            !/^[A-Za-z0-9\s,./#()-]+$/.test(trimmed)
          ) {
            return `${label} contains invalid characters.`;
          }

          break;

        case "email":

          if (
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
              trimmed
            )
          ) {
            return "Please enter a valid email address.";
          }

          break;

        case "any":
        default:
          break;

      }

      return "";
    }
    function handleChange(
      e: React.ChangeEvent<HTMLInputElement>
    ) {
      onValueChange(
        e.target.value
      );

      setLocalError(
        validate(
          e.target.value
        )
      );
    }

    //----------------------------------------------------

    function handleBlur(
      e: React.FocusEvent<HTMLInputElement>
    ) {
      if (readOnly) {
        onComplete?.();
        return;
      }

      const err =
        validate(
          String(value)
        );

      setLocalError(err);

      if (err) {
        requestAnimationFrame(() =>
          e.target.focus()
        );

        return;
      }

      onComplete?.();
    }

    //----------------------------------------------------

    const displayError =
      error ?? localError;

    //----------------------------------------------------

    return (
      <div
        className={`space-y-2 ${containerClassName}`}
      >
        <label className="block text-sm font-medium text-slate-700">
          {label}

          {required && (
            <span className="ml-1 text-red-500">
              *
            </span>
          )}
        </label>

        <div className="flex">

          {prefix && (

            <div
              className={`
                flex
                items-center
                px-3
                py-2.5
                text-sm
                text-slate-600
                bg-slate-100
                border
                border-r-0
                rounded-l-lg
                whitespace-nowrap
                ${displayError
                  ? "border-red-500"
                  : "border-slate-300"
                }
            `}
            >
              {prefix}
            </div>

          )}

          <input
            ref={(el) => {

              if (
                typeof ref === "function"
              ) {
                ref(el);
              }
              else if (ref) {
                ref.current = el;
              }

              if (
                fieldRefs &&
                fieldKey
              ) {
                fieldRefs.current[fieldKey] = el;
              }
            }}
            id={id}
            value={value}
            type={
              validation === "numeric"
                ? "text"
                : validation
            }
            readOnly={readOnly}
            placeholder={placeholder}
            title={tooltip}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={(e) => {

              if (e.key === "Enter") {

                e.preventDefault();

                onComplete?.();

              }

            }}
            {...props}
            className={`
            w-full
            border
            px-3
            py-2.5
            text-sm
            shadow-sm
            transition
            placeholder:text-slate-400
            focus:border-blue-500
            focus:outline-none
            focus:ring-2
            focus:ring-blue-200

            ${displayError
                ? "border-red-500"
                : "border-slate-300"
              }

            ${prefix
                ? "rounded-r-lg rounded-l-none"
                : "rounded-lg"
              }

            ${className}
        `}
          />

        </div>

        {helperText &&
          !displayError && (
            <p className="text-xs text-slate-500">
              {helperText}
            </p>
          )}

        {displayError && (
          <p className="text-xs text-red-500">
            {displayError}
          </p>
        )}
      </div>
    );
  }
);

FormInput.displayName =
  "FormInput";

export default FormInput;